import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';

const AboutUsPage = () => {
  const navigate = useNavigate();

  const teamMembers = [
    {
      name: 'Robert Cepek',
      role: 'Founder & CEO',
      background: 'Agricultural Science bachelor\'s degree, 15+ years commercial growing',
      image: 'team-robert.jpg',
      bio: 'Former head grower at multiple commercial facilities, specialized in IPM and sustainable agriculture practices.'
    }
  ];

  const coreValues = [
    {
      title: 'Sustainability',
      description: 'Promoting eco-friendly IPM practices that protect beneficial insects and reduce chemical dependency',
      icon: 'Leaf'
    },
    {
      title: 'Innovation',
      description: 'Continuously improving our AI-driven tools to stay ahead of evolving pest challenges',
      icon: 'Lightbulb'
    },
    {
      title: 'Farmer Success',
      description: 'Every feature is designed to directly improve crop yields and reduce losses for our growers',
      icon: 'TrendingUp'
    }
  ];

  const whoWeServe = [
    {
      category: 'Commercial Greenhouses',
      description: 'Large-scale operations managing multiple facility locations'
    },
    {
      category: 'Indoor Cultivation Facilities', 
      description: 'Controlled environment agriculture and vertical farms'
    },
    {
      category: 'Home Grow Hobbyists',
      description: 'Personal cultivators and small-scale gardeners'
    },
    {
      category: 'Agriculture Students',
      description: 'Students learning IPM and sustainable agriculture practices'
    },
    {
      category: 'Pest Management Technicians',
      description: 'Professional consultants serving multiple growers'
    }
  ];

  const successStories = [
    {
      company: 'Green Valley Farms',
      result: '40% reduction in pest-related losses',
      quote: 'GrowGuardian helped us prevent a major spider mite outbreak that could have cost us $200K.',
      industry: 'Commercial Greenhouse'
    },
    {
      company: 'Urban Harvest Co',
      result: '60% faster scouting process',
      quote: 'The mobile app streamlined our team\'s scouting workflow across 12 grow rooms.',
      industry: 'Indoor Cultivation'
    },
    {
      company: 'Sustainable Solutions LLC',
      result: '25% increase in beneficial insect effectiveness',
      quote: 'The rotation generator helped us optimize our bio-control program perfectly.',
      industry: 'IPM Consulting'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <img 
                src="/assets/images/GrowGuardianGGlogo-1763327971762.png" 
                alt="GrowGuardian company logo"
                className="w-20 h-20 object-contain"
              />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              About Grow Guardian
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Grow Guardian was founded by growers — not tech companies — with one goal: Make pest prevention simple, clean, and accessible for every grower.
            </p>
            
            <div className="bg-green-50 rounded-lg p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed">
                After years working in commercial cultivation, battling pests, tracking pressures, and training teams, it became clear: <strong>Growers needed a simple tool to help prevent infestations — not just react to them.</strong>
              </p>
              <p className="text-lg text-gray-700 mt-4">
                So Grow Guardian was created.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="bg-green-600 rounded-lg p-8 text-white">
              <div className="flex items-center space-x-3 mb-6">
                <Icon name="Target" size={32} className="text-yellow-400" />
                <h2 className="text-3xl font-bold">Our Mission</h2>
              </div>
              <p className="text-xl text-green-100 leading-relaxed">
                To give growers of all sizes a clean, straightforward tool for scouting, tracking, and preventing pest outbreaks.
              </p>
            </div>

            <div className="bg-blue-600 rounded-lg p-8 text-white">
              <div className="flex items-center space-x-3 mb-6">
                <Icon name="Eye" size={32} className="text-yellow-400" />
                <h2 className="text-3xl font-bold">Our Vision</h2>
              </div>
              <p className="text-xl text-blue-100 leading-relaxed">
                A world where IPM is easy, preventative, and stress-free — powered by smart tools that every grower can understand.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Showcase */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Icon name="Users" size={32} className="text-green-600" />
              <h2 className="text-3xl font-bold text-gray-900">Our Leadership</h2>
            </div>
            <p className="text-lg text-gray-600">Agricultural expertise driving innovation</p>
          </div>

          <div className="max-w-md mx-auto">
            {teamMembers?.map((member, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-sm border border-gray-200 text-center">
                <div className="w-32 h-32 bg-green-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <Icon name="User" size={48} className="text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{member?.name}</h3>
                <div className="text-green-600 font-semibold mb-3 text-lg">{member?.role}</div>
                <div className="text-sm text-gray-600 mb-4">{member?.background}</div>
                <p className="text-gray-700 leading-relaxed">{member?.bio}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 bg-green-50 rounded-lg p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Built by Growers, For Growers
            </h3>
            <p className="text-gray-700 text-lg">
              Our founder brings decades of hands-on commercial growing experience to every feature we develop.
            </p>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Icon name="Heart" size={32} className="text-green-600" />
              <h2 className="text-3xl font-bold text-gray-900">Our Values</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coreValues?.map((value, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-sm border border-gray-200 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-lg mx-auto mb-6 flex items-center justify-center">
                  <Icon name={value?.icon} size={24} className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{value?.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Icon name="Building2" size={32} className="text-green-600" />
              <h2 className="text-3xl font-bold text-gray-900">Who We Serve</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whoWeServe?.slice(0, 3)?.map((category, index) => (
              <div key={index} className="bg-green-50 rounded-lg p-6 border border-green-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{category?.category}</h3>
                <p className="text-gray-600">{category?.description}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 max-w-4xl mx-auto">
            {whoWeServe?.slice(3)?.map((category, index) => (
              <div key={index} className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{category?.category}</h3>
                <p className="text-gray-600">{category?.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 bg-green-600 rounded-lg p-8 text-white">
            <h3 className="text-xl font-bold mb-4">Anyone trying to protect their plants</h3>
            <p className="text-green-100 text-lg">
              From small hobby gardens to commercial operations, we serve anyone committed to healthy, productive crops.
            </p>
          </div>
        </div>
      </section>

      {/* Built from Real Experience */}
      <section className="py-16 bg-gradient-to-r from-green-900 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Icon name="Shield" size={32} className="text-yellow-400" />
              <h2 className="text-3xl font-bold">Built from Real Experience</h2>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <p className="text-xl text-green-100 mb-6 leading-relaxed text-center">
              Grow Guardian wasn't built from theory. It was born in actual grow rooms — dealing with mites, aphids, mildew, root pathogens, inspectors, and state compliance.
            </p>
            <p className="text-lg text-green-100 text-center mb-8">
              We built the tool we wish existed years ago.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
              {[
                { pest: 'Spider Mites', icon: 'Bug' },
                { pest: 'Aphids', icon: 'Leaf' },
                { pest: 'Powdery Mildew', icon: 'Cloud' },
                { pest: 'Root Pathogens', icon: 'Zap' }
              ]?.map((item, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                  <Icon name={item?.icon} size={32} className="text-yellow-400 mx-auto mb-3" />
                  <div className="text-white font-medium">{item?.pest}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Icon name="Star" size={32} className="text-green-600" />
              <h2 className="text-3xl font-bold text-gray-900">Customer Success Stories</h2>
            </div>
            <p className="text-lg text-gray-600">Real results from growers using GrowGuardian</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {successStories?.map((story, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="mb-4">
                  <div className="text-2xl font-bold text-green-600 mb-2">{story?.result}</div>
                  <div className="text-sm text-gray-500">{story?.industry}</div>
                </div>
                <blockquote className="text-gray-700 italic mb-4">"{story?.quote}"</blockquote>
                <div className="text-sm font-semibold text-gray-900">— {story?.company}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Approach */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Icon name="Cpu" size={32} className="text-green-600" />
              <h2 className="text-3xl font-bold text-gray-900">Our Technology Approach</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">AI-Driven Insights</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Icon name="Check" size={16} className="text-green-600" />
                  <span className="text-gray-700">Machine learning algorithms analyze pest patterns</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="Check" size={16} className="text-green-600" />
                  <span className="text-gray-700">Predictive modeling for outbreak prevention</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="Check" size={16} className="text-green-600" />
                  <span className="text-gray-700">Personalized rotation recommendations</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Real-Time Monitoring</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Icon name="Check" size={16} className="text-green-600" />
                  <span className="text-gray-700">Live dashboard updates across all facilities</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="Check" size={16} className="text-green-600" />
                  <span className="text-gray-700">Instant alert system for critical issues</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="Check" size={16} className="text-green-600" />
                  <span className="text-gray-700">Cloud synchronization across devices</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-green-600 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <Icon name="MessageCircle" size={32} className="text-yellow-400" />
            <h2 className="text-3xl font-bold">Get in Touch</h2>
          </div>
          
          <div className="max-w-2xl mx-auto mb-8">
            <p className="text-xl text-green-100 mb-6">
              Ready to transform your IPM approach? Our team is here to help you get started.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <Icon name="Phone" size={24} className="text-yellow-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2">Sales Consultation</h3>
              <p className="text-green-100 text-sm">Discuss your specific growing needs</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <Icon name="Headphones" size={24} className="text-yellow-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2">Technical Support</h3>
              <p className="text-green-100 text-sm">Get help with setup and troubleshooting</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <Icon name="Handshake" size={24} className="text-yellow-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2">Partnership Inquiries</h3>
              <p className="text-green-100 text-sm">Explore integration opportunities</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/')}
              className="bg-white text-green-600 hover:bg-gray-50 px-8 py-3"
            >
              Back to Home
            </Button>
            <Button 
              onClick={() => navigate('/ipm-operations-overview')}
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-8 py-3"
            >
              View Analytics Dashboard
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUsPage;